// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductPrice, { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";

import LastPurchasedOn from "@hardwoods/App/Components/Common/LastPurchasedOn";
import ProductAddToCartButton from "@hardwoods/Overrides/Components/ProductAddToCartButton";
import ProductImage, { ProductImageStyles } from "@hardwoods/Overrides/Components/ProductImage";
import ProductQuantityOrdered, {
    ProductQuantityOrderedStyle,
} from "@hardwoods/Overrides/Components/ProductQuantityOrdered";
import { partNumberStyle } from "@hardwoods/Overrides/Widgets/ProductList/helper";
import { fontConfig } from "@hardwoods/XDL/PreStyle/typography";
import { ellipsis } from "@hardwoods/XDL/Utilities";
import { hideElement } from "@hardwoods/XDL/Utilities/SharedStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    return {
        productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
        settingsCollection: getSettingsCollection(state),
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    showInventory: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    hideProductPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureWrapper?: GridItemProps;
    productQuantityOrderedStyles?: ProductQuantityOrderedStyle;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    priceInventoryWrapper?: GridContainerProps;
    availabilityStyles?: ProductAvailabilityStyles;
    minPackQuantityWrapper?: InjectableCss;
    minPackQuantityLabelText?: TypographyPresentationProps;
    minPackQuantityValueText?: TypographyPresentationProps;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 10px;
        `,
    },
    productBrandStyles: {
        logoImage: {
            width: "80px",
            css: css`
                img {
                    max-height: 40px;
                    max-width: 100%;
                }
            `,
        },
        nameLink: {
            typographyProps: {
                css: css`
                    display: flex;
                `,
            },
            css: css`
                width: auto;
            `,
        },
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                margin-bottom: 15px;
                position: relative;
                height: 230px;
                width: 100%;
            `,
        },
        image: {
            css: css`
                width: 100%;
                height: 100%;
                img {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: auto;
                    height: auto;
                    max-width: 100%;
                    max-height: 100%;
                }
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
                min-height: 54px;
            `,
            typographyProps: {
                size: 14,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 20px;
                    ${ellipsis(2)}
                `,
            },
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 10px;
            `,
        },
        erpNumberLabelText: partNumberStyle.label,
        erpNumberValueText: partNumberStyle.value,
        manufacturerItemLabelText: partNumberStyle.label,
        manufacturerItemValueText: partNumberStyle.value,
        customerNameLabelText: partNumberStyle.label,
        customerNameValueText: partNumberStyle.value,
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            font-size: 18px;
            margin-right: 15px;
            margin-bottom: 5px;
        `,
    },
    productPriceStyles: {
        netPrice: { priceText: { size: 16, lineHeight: "20px", weight: fontConfig.weight.black } },
        price: {
            priceText: { size: 14, lineHeight: "18px", weight: fontConfig.weight.black },
            unitOfMeasureText: {
                size: 12,
                lineHeight: "18px",
                weight: "normal",
                css: css``,
            },
            signInLink: {
                css: css`
                    margin-top: 5px;
                `,
            },
        },
    },
    availabilityStyles: {
        container: {
            css: css`
                padding-top: 5px;
                flex: 1 1 auto;
                word-break: break-word;
                width: auto;
                margin-bottom: 5px;
            `,
        },
        messageGridItem: {
            css: css`
                justify-content: flex-start;
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        sizeVariant: "small",
    },
    addToCartContainer: {
        gap: 10,
        offsetCss: css`
            align-items: center;
        `,
        css: css`
            margin: 15px 0 20px 0;
            align-items: flex-end;
            display: flex;
            &&& > div {
                flex-wrap: nowrap;
            }
        `,
    },
    qtyGridItem: {
        width: 3,
        css: css`
            &&& {
                width: auto;
                max-width: none;
            }
        `,
    },
    uomGridItem: {
        width: 6,
    },
    productQuantityOrderedStyles: {
        qtyField: {
            cssOverrides: {
                formField: css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.formField")};
                    width: 65px;
                `,
            },
        },
    },
    productUnitOfMeasureWrapper: {
        css: css`
            &&& {
                margin-top: -6px;
            }
        `,
    },
    addToCartButtonGridItem: {
        width: 3,
        css: css`
            &&&& {
                flex-grow: 1;
                max-width: none;
                width: auto;
            }
            button {
                padding: 0;
            }
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
            &&&.button-wrapper-icon-only {
                width: 54px;
            }
        `,
    },
    priceInventoryWrapper: {
        css: css`
            margin-top: 15px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            width: 100%;
            align-items: flex-end;
        `,
    },
    minPackQuantityLabelText: {
        css: css`
            font-size: 12px;
            margin-right: 5px;
            &::after {
                content: ":";
            }
        `,
    },
    minPackQuantityValueText: {
        weight: "bold",
        css: css`
            font-size: 12px;
            flex: 1 1 auto;
            min-width: 0;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

productCarouselProductStyles.hideProductPartNumbersStyles = {
    ...productCarouselProductStyles.productPartNumbersStyles,
    container: {
        css: css`
            flex-grow: 0;
            margin-bottom: 5px;
        `,
    },
    erpNumberGridItem: {
        width: 0,
        css: css`
            ${hideElement}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    settingsCollection,
    showInventory,
    isAuthenticated,
    actionsTemplate,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    const { productSettings } = settingsCollection;
    showInventory = showInventory && productSettings.showInventoryAvailability;
    const showUOM = !!(
        showPrice &&
        product.unitOfMeasures &&
        product.unitOfMeasures.filter(uom => uom.unitOfMeasure).length > 1 &&
        productSettings.alternateUnitsOfMeasure
    );

    const hasSamePrices =
        productContext?.productInfo?.pricing?.additionalResults?.priceUOMNetPrice ===
        productContext?.productInfo?.pricing?.unitNetPriceDisplay;

    const isVariantParent = productContext?.product?.isVariantParent;
    const minPackQuantityProduct = Number(productContext?.productInfo?.pricing?.additionalResults?.minPackQuantity);

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    return (
        <>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper>
                    {showBrand && (
                        <StyledWrapper {...styles.productBrandWrapper}>
                            {product.brand && (
                                <ProductBrand
                                    brand={product.brand}
                                    showLogo
                                    extendedStyles={styles.productBrandStyles}
                                />
                            )}
                            <LastPurchasedOn
                                extendedStyles={{
                                    container: {
                                        css: css`
                                            margin-left: auto;
                                        `,
                                    },
                                }}
                            />
                        </StyledWrapper>
                    )}
                    {showImage && !showOverlayBadge && (
                        <ProductImage
                            product={productContext}
                            onClick={() => trackProductRecommendation(true)}
                            extendedStyles={styles.productImageStyles}
                        />
                    )}
                    {showImage && showOverlayBadge && overlayBadge && (
                        <StyledWrapper
                            {...styles.badgeAreaWrapper}
                            ref={badgeAreaWrapperRef}
                            data-test-selector="productCarouselBadge"
                        >
                            <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                            <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                <Badge
                                    type={overlayBadge.badgeType}
                                    value={
                                        overlayBadge.badgeType === "Image"
                                            ? overlayBadge.largeImageBadgePath
                                            : overlayBadge.displayText
                                    }
                                    badgeTextColor={overlayBadge.textColorHexCode}
                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                    imageAltText={
                                        overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                    }
                                    textBadgeCss={styles.badgeText}
                                    imageBadgeProps={styles.badgeImageProps}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {showPartNumbers && (
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    )}
                    {showTitle && (
                        <ProductDescription
                            onClick={() => trackProductRecommendation(true)}
                            product={productContext}
                            extendedStyles={styles.productDescriptionStyles}
                        />
                    )}
                    {!!minPackQuantityProduct && minPackQuantityProduct !== 9999 && (
                        <StyledWrapper {...styles.minPackQuantityWrapper}>
                            <Typography {...styles.minPackQuantityLabelText}>
                                {siteMessage("Hardwoods_LiftQty")}
                            </Typography>
                            <Typography {...styles.minPackQuantityValueText}>
                                {minPackQuantityProduct.toLocaleString()}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {showPartNumbers && (
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            showCustomerName={true}
                            showManufacturerItem={false}
                            extendedStyles={styles.hideProductPartNumbersStyles}
                        />
                    )}
                    {showTextBadges && textBadges && (
                        <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                    )}
                    {showImageBadges && imageBadges && (
                        <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                    )}
                </StyledWrapper>
                <StyledWrapper>
                    {(showPrice || showInventory) && (
                        <StyledWrapper {...styles.priceInventoryWrapper}>
                            {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    {!hasSamePrices && (
                                        <ProductPrice
                                            showLabel={false}
                                            product={productContext}
                                            showPriceUOM={true}
                                            extendedStyles={styles.productPriceStyles}
                                        />
                                    )}
                                    <StyledWrapper className={hasSamePrices ? "hasSamePrices" : ""}>
                                        <ProductPrice
                                            showLabel={false}
                                            product={productContext}
                                            showPriceUOM={!!isVariantParent}
                                            showSignIn={true}
                                            extendedStyles={styles.productPriceStyles}
                                        />
                                    </StyledWrapper>
                                    <ProductQuantityBreakPricing
                                        extendedStyles={styles.productQuantityBreakPricingStyles}
                                    />
                                </StyledWrapper>
                            )}
                            {showInventory && <ProductContextAvailability extendedStyles={styles.availabilityStyles} />}
                        </StyledWrapper>
                    )}
                    {showAddToCart && isAuthenticated && (
                        <GridContainer {...styles.addToCartContainer}>
                            <GridItem {...styles.qtyGridItem}>
                                <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                    {translate("QTY_quantity")}
                                </VisuallyHidden>
                                <ProductQuantityOrdered
                                    hideLabel
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                    id={`${carouselId}-qty`}
                                    hideQtyUom={showUOM}
                                />
                            </GridItem>
                            {showUOM && (
                                <GridItem {...styles.uomGridItem}>
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        id={`${carouselId}-uom`}
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                    />
                                </GridItem>
                            )}
                            <GridItem {...styles.addToCartButtonGridItem}>
                                <ProductAddToCartButton
                                    onAddToCartClick={() => trackProductRecommendation(false)}
                                    iconOnly={showUOM}
                                    data-test-selector="addToCartBtn"
                                    extendedStyles={styles.productAddToCartButtonStyles}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                    {showAddToList && isAuthenticated && (
                        <ProductAddToListLink
                            data-test-selector="addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                        />
                    )}
                </StyledWrapper>
                {actionsTemplate ? actionsTemplate(product) : []}
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
